<template>
  <mu-card id="blood-glucose-input">
    <mu-card-text class="card-style" v-bind:class="{cardHasData: hasData}">
      <div v-if="loading">
        <mu-flexbox class="card-loading-padding" align="center" justify="center">
          <mu-flexbox-item align="center">
            <mu-circular-progress :size="56" color="#FFFFFF" :strokeWidth="5"/>
          </mu-flexbox-item>
        </mu-flexbox>
      </div>
      <div v-if="!loading" class="animated fadeIn">
        <mu-flexbox v-if="data.no_setting || data.error" class="card-error-padding" justify="center">
          <mu-flexbox-item align="center">
            <div class="card-notice-title" v-text="$t('message.cardEmpty')"></div>
            <div>{{ $t('message.cardTapPrefix') }}
              <mu-icon v-if="data.no_setting" class="card-icon-error" :size="16" slot="left" value="settings"/>
              <mu-icon v-if="data.error" class="card-icon-error" :size="16" slot="left" value="add"/>
              <span v-if="data.error || data.no_setting">{{ $t('message.cardTapSuffix') }}</span>
            </div>
          </mu-flexbox-item>
        </mu-flexbox>
        <div v-else @click="goToDetail" style="cursor: pointer">
          <span v-show="false">{{hasData = true}}</span>
          <mu-content-block align="right">
            <mu-flexbox orient="horizontal" justify="center">
              <mu-flexbox-item class="card-lite-title">
                <span v-text="latestReadingTitle"></span>
              </mu-flexbox-item>
              <mu-flexbox-item>
                <div class="card-large-stat" align="right">
                  <span v-text="data.value + ' ' + data.measure"></span>
                  <span v-if="data.userDevice && data.userDevice.userDeviceId !== 0">
                    <img v-tooltip.left="'From device: ' + data.userDevice.nickname"
                         style="position: relative; top: 6px;margin-left: 4px;" width="22"
                         src="/static/ic_devices_bg@3x.png"/>
                  </span>
                </div>
              </mu-flexbox-item>
            </mu-flexbox>
          </mu-content-block>
          <mu-content-block>
            <mu-flexbox>
              <mu-flexbox-item class="card-lite-title">
                <span v-text="lastReadingAtTitle"></span>
              </mu-flexbox-item>
              <mu-flexbox-item align="right">
                <div v-text="formatDate(data.date)"/>
                <mu-icon class="card-icon-error" :size="16" slot="left" value="add"/>
              </mu-flexbox-item>
            </mu-flexbox>
          </mu-content-block>
          <mu-content-block>
            <mu-flexbox>
              <mu-flexbox-item class="card-lite-title">
                <span v-text="inRangeTitle"></span>
              </mu-flexbox-item>
              <mu-flexbox-item align="right">
                <div v-text="data.in_range_today"/>
                <mu-icon class="card-icon-error" :size="16" slot="left" value="add"/>
              </mu-flexbox-item>
            </mu-flexbox>
          </mu-content-block>
          <mu-content-block>
            <mu-flexbox>
              <mu-flexbox-item class="card-lite-title">
                <span v-text="averageTitle"></span>
              </mu-flexbox-item>
              <mu-flexbox-item align="right">
                <div v-text="data.average + ' ' + data.measure"/>
                <mu-icon class="card-icon-error" :size="16" slot="left" value="add"/>
              </mu-flexbox-item>
            </mu-flexbox>
          </mu-content-block>
        </div>
      </div>
    </mu-card-text>
    <mu-card-actions>
      <mu-flexbox justify="flex_start" style="height: 48px">
        <svg class="card-left-icon" width="14px" height="16px" viewBox="26 191 11 16" version="1.1"
             xmlns="http://www.w3.org/2000/svg">
          <path
            d="M36.3873266,202.013826 C36.3873266,201.047215 36.0280503,199.003253 34.5350319,197.082478 C33.2459103,195.424041 31.1433712,191 31.1433712,191 C31.1433712,191 29.1414164,195.424041 27.8522947,197.082448 C26.3592763,199.003223 26,201.047186 26,202.013826 C26,204.564452 28.2936491,206.63741 31.1433712,206.685209 L31.1433712,206.686322 C31.1601681,206.686322 31.1768993,206.685883 31.1936633,206.685766 C31.2104602,206.685912 31.2271914,206.686322 31.2439883,206.686322 L31.2439883,206.685209 C34.0936775,206.63741 36.3873266,204.564452 36.3873266,202.013826 L36.3873266,202.013826 Z"
            id="Shape" stroke="none" fill="#E74C3C" fill-rule="evenodd"></path>
        </svg>
        <mu-flexbox-item class="left16" v-text="loading ? $t('message.loadingText') : title"></mu-flexbox-item>
        <mu-flexbox-item align="right" v-if="data.no_setting" v-bind:class="{invisible: isOpen || loading}">
          <mu-icon-button
            class="card-bar-icon"
            icon="settings"
            @click.native="open"
          ></mu-icon-button>
        </mu-flexbox-item>
        <mu-flexbox-item
          align="right"
          v-if="!data.no_setting"
        >
          <mu-icon-button
            :disabled="loading"
            v-if="!isOpenInput"
            v-tooltip.top="isOpenInput ? $t('message.closeText') : $t('message.addEntryText')"
            class="card-bar-icon"
            :icon="'add'"
            @click.native="openInput"
          />
          <mu-icon-button
            :disabled="loading"
            v-if="isOpenInput"
            class="card-bar-icon"
            :icon="'remove'"
            @click.native="openInput"
          />
        </mu-flexbox-item>
      </mu-flexbox>
    </mu-card-actions>
    <form v-on:submit.prevent="saveSettings" v-if="isOpen && data.no_setting">
      <mu-card-text>
        <mu-select-field
          v-validate
          data-vv-validate-on="none"
          :data-vv-rules="'required'"
          data-vv-name="blood_glucose_setting"
          data-vv-value-path="inputValue"
          data-vv-as="Blood glucose settings"
          :errorText="errors.first('blood_glucose_setting')"
          v-if="data.missing_settings.blood_glucose"
          v-model="settings.blood_glucose"
          label="BLOOD GLUCOSE MEASUREMENT"
          :fullWidth="true"
          :scroller="scroller"
          separator=", "
        >
          <mu-menu-item
            v-for="item, index in data.missing_settings.blood_glucose"
            :key="'blood-glucose-setting-' + item"
            :value="item.preference_id"
            :title="item.unit"
          />
        </mu-select-field>
        <div v-if="data.missing_settings.glucose_range">
          <vue-slider v-bind="sliderSettings" v-model="sliderSettings.value"></vue-slider>
        </div>
        <mu-select-field
          v-validate
          data-vv-validate-on="none"
          :data-vv-rules="'required'"
          data-vv-name="hba1c_setting"
          data-vv-value-path="inputValue"
          data-vv-as="hbA1c settings"
          :errorText="errors.first('hba1c_setting')"
          v-if="data.missing_settings.hba1c"
          v-model="settings.hba1c"
          label="HBA1C MEASUREMENT"
          :fullWidth="true"
          :scroller="scroller"
        >
          <mu-menu-item
            v-for="item, index in data.missing_settings.hba1c"
            :key="'hba1c-setting-' + item"
            :value="item.preference_id"
            :title="item.unit"
          />
        </mu-select-field>
      </mu-card-text>
      <mu-card-actions align="right">
        <mu-flat-button
          :label="$t('message.discardText')"
          @click.native="close"
          class="demo-flat-button"
        />
        <mu-flat-button
          type="submit"
          @click.native="saveSettings"
          :disabled="isSaving"
          :label="isSaving ? $t('message.savingText'): $t('message.saveText')"
          class="demo-flat-button"
        />
      </mu-card-actions>
    </form>
    <transition :css="false" @enter="onEnter" @leave="onLeave">
      <form style="overflow: hidden" v-bind:style="{height: computedAuto}" @submit.prevent="saveReading"
            v-if="isOpenInput" novalidate>
        <mu-card-text>
          <mu-row>
            <mu-col width="100" tablet="50" desktop="50" v-if="bloodGlucose.measure === 'mgdl'">
              <mu-text-field
                type="number"
                v-validate
                min="0"
                max="540"
                data-vv-validate-on="none"
                :data-vv-rules="'min_value:0|max_value:540'"
                data-vv-as="mg/dL"
                data-vv-name="mgdl"
                data-vv-value-path="inputValue"
                :errorText="errors.first('mgdl')"
                :label="$t('message.labelBloodSugar')"
                v-model.number="model.blood_glucose_mgdl"
                :hintText="$t('message.placeholderText')"
                :fullWidth="true"
                :helpText="bloodGlucose.unit"
              />
            </mu-col>
            <mu-col width="100" tablet="50" desktop="50" v-if="bloodGlucose.measure === 'mmoll'">
              <mu-text-field
                type="number"
                v-validate
                min="0"
                max="30"
                data-vv-validate-on="none"
                data-vv-as="mmol/L"
                :data-vv-rules="'min_value:0|max_value:30'"
                data-vv-name="mmoll"
                data-vv-value-path="inputValue"
                :errorText="errors.first('mmoll')"
                :label="$t('message.labelBloodSugar')"
                v-model.number="model.blood_glucose_mmoll"
                :hintText="$t('message.placeholderText')"
                :fullWidth="true"
                :helpText="bloodGlucose.unit"
              />
            </mu-col>
            <mu-col width="100" tablet="50" desktop="50" v-if="hba1c.measure === 'percentage'">
              <mu-text-field
                type="number"
                v-validate
                min="0"
                max="25"
                data-vv-validate-on="none"
                :data-vv-rules="'min_value:0|max_value:25'"
                data-vv-name="hba1c_percent"
                data-vv-as="%"
                data-vv-value-path="inputValue"
                :errorText="errors.first('hba1c_percent')"
                :label="$t('message.labelHba1cOptional')"
                v-model.number="model.hba1c_percent"
                :hintText="$t('message.placeholderText')"
                :fullWidth="true"
                :helpText="hba1c.unit"
              />
            </mu-col>
            <mu-col width="100" tablet="50" desktop="50" v-if="hba1c.measure === 'mmol-mol'">
              <mu-text-field
                type="number"
                v-validate
                min="0"
                max="300"
                data-vv-validate-on="none"
                :data-vv-rules="'min_value:0|max_value:300'"
                data-vv-name="hba1c_mmol_mol"
                :data-vv-as="hba1c.unit"
                data-vv-value-path="inputValue"
                :errorText="errors.first('hba1c_mmol_mol')"
                :label="$t('message.labelHba1cOptional')"
                v-model.number="model.hba1c_mmol_mol"
                :hintText="$t('message.placeholderText')"
                :fullWidth="true"
                :helpText="hba1c.unit"
              />
            </mu-col>
          </mu-row>
          <mu-select-field
            v-model="model.tag"
            :label="$t('message.labelTagYourReading')"
            :fullWidth="true"
            :maxHeight="300"
            :scroller="scroller"
          >
            <mu-menu-item
              v-for="tag, index in readingTags"
              :key="'reading-tag' + index"
              :value="tag"
              :title="$t(localizeTag(tag.tag))"
            />
          </mu-select-field>


          <mu-checkbox v-model="model.customDate" :label="$t('message.labelCustomDate')"></mu-checkbox>
          <date-picker v-if="model.customDate" type="datetime" v-model="model.date"></date-picker>

        </mu-card-text>
        <mu-card-actions align="right">
          <!--Fix duplicate entrys on form submit -->
          <button type="submit" style="display: none;"></button>

          <mu-flat-button
            :label="$t('message.discardText')"
            @click.native="closeInput"
            class="demo-flat-button"
          />
          <mu-flat-button
            type="button"
            @click.native="saveReading"
            :disabled="isSaving"
            :label="isSaving ? $t('message.savingText'): $t('message.saveText')"
            class="demo-flat-button"
          />
        </mu-card-actions>
      </form>

    </transition>
  </mu-card>
</template>
<script type="text/babel">


  import { TweenMax, TweenLite, Power3, ScrollToPlugin } from 'gsap/all'
  import 'gsap/ScrollToPlugin'
  const plugins = [ ScrollToPlugin];


  import BloodGlucoseApi from '../../../../api/Tracking/BloodGlucose'
  import HbA1cApi from '../../../../api/Tracking/HbA1c'
  import SettingsApi from '../../../../api/Settings'
  import ListsApi from '../../../../api/Lists'
  import CardAnimationMixin from '../../../../mixins/CardAnimationMixin'
  import TagLocalizerMixin from '../../../../mixins/Localization/BgTags/TagLocalizerMixin'
  import vueSlider from 'vue-slider-component/src/vue2-slider.vue'
  import DatePicker from "../../../DateTimePicker/DateTimePicker.vue";

  export default {
    name: 'blood-glucose-input',
    mixins: [CardAnimationMixin, TagLocalizerMixin],
    components: {
      DatePicker,
      vueSlider
    },
    props: {
      bloodGlucose: {},
      hba1c: {}
    },
    data() {
      return {
        title: this.$t('message.cardBloodGlucose'),
        latestReadingTitle: this.$t('message.cardLatestReading'),
        lastReadingAtTitle: this.$t('message.cardLatestReadingAt'),
        isOpen: false,
        isOpenInput: false,
        isSaving: false,
        isBackgroundShown: true,
        hasData: false,
        noData: false,
        loading: true,
        data: {},
        value: [0, 5],
        settings: {},
        scroller: document.getElementsByClassName('main-content')[0],
        sliderSettings: {
          value: [4, 6],
          width: '100%',
          height: 8,
          dotSize: 16,
          min: 0,
          max: 500,
          disabled: false,
          show: true,
          tooltip: 'always',
          formatter: '{value}',
          bgStyle: {
            backgroundColor: '#fff',
            boxShadow: 'inset 0.5px 0.5px 3px 1px rgba(0,0,0,.36)'
          },
          tooltipStyle: {
            backgroundColor: '#E74C27',
            borderColor: '#E74C27'
          },
          processStyle: {
            backgroundColor: '#E74C27'
          }
        },
        readingTags: [],
        model: {
          blood_glucose_mmoll: null,
          blood_glucose_mgdl: null,
          hba1c_percent: null,
          hba1c_mmol_mol: null,
          tags: [],
          tag: null,
        }
      }
    },
    mounted() {
      let self = this;
      self.loadTags();
      self.loadData();
      self.averageTitle = self.$t('message.cardBloodGlucose30DayAverage');
      self.inRangeTitle = self.$t('message.cardBloodGlucoseInRange');

      self.$EventBus.$on('lc:settingsUpdated', function () {
        self.loadData();
      });

      self.$EventBus.$on('bgPusherReceived', function (data) {
        self.$nextTick(function () {
          self.data = data;
          self.$forceUpdate();
        });
      });

      self.$EventBus.$on('bgReadingUpdated', function () {
        self.$store.commit('setBloodGlucoseCard', {});
        self.loadData();
      })

    },
    methods: {
      onEnter(el, done) {
        TweenMax.from(el, 0.3, {
            height: 0,
            immediateRender: true,
            onComplete: done
          }
        )
      },
      onLeave(el, done) {
        TweenMax.to(el, 0.3, {
          height: 0,
          immediateRender: true,
          onComplete: done
        })
      },
      formatDate(date) {
        return moment(date).subtract(30, 'seconds').fromNow();
      },
      saveReading() {
        let self = this;

        self.$validator.validateAll()
          .then(function (valid) {

            if (self.model.tag !== null) {
              self.model.tags = [self.model.tag];
            }

            if (valid) {

              if (!_.has(self.model, 'date')) {
                self.model.date = moment().format();
              }

              let hasBloodGlucose = false;
              let hasHba1c = false;

              /**
               * Check is user setting is mmol/l and has set field
               */
              if (self.bloodGlucose.measure === 'mmoll') {
                if (self.model.blood_glucose_mmoll !== null) {
                  hasBloodGlucose = true;
                  if (self.hba1c.measure === 'percentage') {
                    if (self.model.hba1c_percent !== null) {
                      hasHba1c = true;
                    }
                  }
                  if (self.hba1c.measure === 'mmol-mol') {
                    if (self.model.hba1c_mmol_mol !== null) {
                      hasHba1c = true;
                    }
                  }
                }
                else {
                  hasBloodGlucose = false;
                  if (self.hba1c.measure === 'percentage') {
                    if (self.model.hba1c_percent !== null) {
                      hasHba1c = true;
                    }
                  }
                  if (self.hba1c.measure === 'mmol-mol') {
                    if (self.model.hba1c_mmol_mol !== null) {
                      hasHba1c = true;
                    }
                  }
                }
              }
              /**
               * Check if user setting is mg/dL and has set field
               */
              if (self.bloodGlucose.measure === 'mgdl') {
                if (self.model.blood_glucose_mgdl !== null) {
                  hasBloodGlucose = true;
                  if (self.hba1c.measure === 'percentage') {
                    if (self.model.hba1c_percent !== null) {
                      hasHba1c = true;
                    }
                  }
                  if (self.hba1c.measure === 'mmol-mol') {
                    if (self.model.hba1c_mmol_mol !== null) {
                      hasHba1c = true;
                    }
                  }
                }
                else {
                  hasBloodGlucose = false;
                  if (self.hba1c.measure === 'percentage') {
                    if (self.model.hba1c_percent !== null) {
                      hasHba1c = true;
                    }
                  }
                  if (self.hba1c.measure === 'mmol-mol') {
                    if (self.model.hba1c_mmol_mol !== null) {
                      hasHba1c = true;
                    }
                  }
                }
              }

              /**
               * Unset hba1c from main post
               */
              if (hasHba1c) {
                self.isSaving = true;
                let model = {};
                if (_.has(self.model, 'hba1c_percent')) {
                  model.date = self.model.date;
                  model.percent = self.model.hba1c_percent;
                }
                if (_.has(self.model, 'hba1c_mmol_mol')) {
                  model.date = self.model.date;
                  model.mmol_mol = self.model.hba1c_mmol_mol;
                }

                let postHba1c = HbA1cApi.postHba1cTracking(model)
                  .then(function (response) {
                    self.isSaving = true;
                    self.$store.commit('setBloodGlucoseCard', {});
                    self.$toastr.success("Hba1c reading added");
                    if (self.$route.name === 'Dashboard') {
                      TweenLite.to(self.scroller, 0.2, {
                        scrollTo: {
                          y: self.$el,
                          offsetY: 16
                        }
                      })
                    }
                    if (!hasBloodGlucose) {
                      self.model = {
                        blood_glucose_mmoll: null,
                        blood_glucose_mgdl: null,
                        hba1c_percent: null,
                        hba1c_mmol_mol: null,
                        tags: []
                      };
                      self.isSaving = false;
                      self.loadData();
                    }
                  })
              }

              if (hasBloodGlucose) {
                self.isSaving = true;
                if (_.has(self.model, 'hba1c_percent')) {
                  delete self.model.hba1c_percent;
                }
                if (_.has(self.model, 'hba1c_mmol_mol')) {
                  delete self.model.hba1c_mmol_mol;
                }


                let postData = BloodGlucoseApi.postBloodGlucoseTracking(self.model);
                postData.then(function (response) {
                  self.$store.commit('setBloodGlucoseCard', {});
                  self.$gtag.event('ReadingAdded',{
                    'event_category': 'BloodGlucoseCard',
                    'event_label': 'Added reading',
                    'value': 1
                  });
                  self.$toastr.success(self.$t('message.cardBloodGlucoseAdded'));
                  if (self.$route.name === 'Dashboard') {
                    TweenLite.to(self.scroller, 0.2, {
                      scrollTo: {
                        y: self.$el,
                        offsetY: 16
                      }
                    })
                  }
                  self.model = {
                    blood_glucose_mmoll: null,
                    blood_glucose_mgdl: null,
                    hba1c_percent: null,
                    hba1c_mmol_mol: null,
                    tags: []
                  };
                  self.isSaving = false;
                  self.loadData();
                })
              }
            }
            else {
              self.isSaving = false;
            }
          })
          .catch(function () {
            self.isSaving = false;
          })
      },
      loadTags() {
        let self = this;
        if (self.$store.state.bgTagList.length > 0) {
          self.readingTags = self.$store.state.bgTagList;
        }
        else {
          let getData = ListsApi.getTagList();
          getData.then(function (response) {
            let data = response.data;
            self.$store.commit('setBgTagList', data);
            data = data.sort(function (a, b) {
              let textA = a.tag.toUpperCase();
              let textB = b.tag.toUpperCase();
              return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
            });
            self.readingTags = data
          });
        }
      },
      loadData() {
        let self = this;
        self.loading = true;
        self.isSaving = false;
        self.isOpen = false;
        self.isOpenInput = false;
        if (_.has(self.$store.state.cards.bloodGlucose, 'value')) {
          self.loading = false;
          self.data = self.$store.state.cards.bloodGlucose;
        }
        else {
          let getData = BloodGlucoseApi.getBloodGlucoseTrackingCard();
          getData.then(function (response) {
              const status = response.status;
              if (status === 200) {
                const data = response.data;
                self.loading = false;
                self.data = data;
                if (_.has(self.data, 'missing_settings.glucose_range')) {
                  self.sliderSettings.min = 0;
                  self.sliderSettings.max = self.data.missing_settings.glucose_range.max_value;
                  let measure = self.data.missing_settings.glucose_range.measure;
                  let unit;
                  if (measure === 'mgdl') {
                    self.sliderSettings.value = [72, 108];
                    unit = 'mg/dL'
                  }
                  else {
                    self.sliderSettings.value = [4, 6];
                    unit = "mmol/L"
                  }
                  self.sliderSettings.formatter = "{value} " + unit;
                  self.isBackgroundShown = false
                }
                else {
                  if (_.has(self.data, 'value')) {
                    self.$store.commit('setBloodGlucoseCard', response.data);
                  }
                }
              }
              else {
                if (status === 201) {
                  const data = response.data;
                  if (data) {
                    self.data = data;
                    self.loading = false
                  } else {
                    self.loading = false
                  }
                }
              }
            }
          )
        }
      },
      saveSettings() {
        let self = this;
        self.$validator.validateAll()
          .then(function (valid) {
            if (valid) {
              if (_.has(self.data, 'missing_settings.glucose_range')) {
                self.isSaving = true;
                let min = self.sliderSettings.value[0];
                let max = self.sliderSettings.value[1];
                let body = {
                  glucose_range: {
                    min: min,
                    max: max
                  }
                };
                SettingsApi.updateUserSettings(body)
                  .then(function (response) {
                    self.$EventBus.$emit('lc:settingsUpdated', true);
                  }, function (error) {
                    self.data = error;
                    self.isSaving = false;
                    self.loading = false
                  })
              }
              else if ((self.settings.hba1c !== 0) || (self.settings.blood_glucose !== 0)) {
                self.isSaving = true;
                SettingsApi.updateUserSettings(self.settings)
                  .then(function (response) {
                    self.isSaving = false;
                    self.$EventBus.$emit('lc:settingsUpdated', true);
                  }, function (error) {
                    self.data = error;
                    self.isSaving = false;
                    self.loading = false
                  })
              }
            }
            else {
              self.isSaving = false;
            }
          })
          .catch(function () {
            self.isSaving = false;
          })
      },
      open() {
        this.isOpen = true
      },
      close() {
        this.isOpen = false
      },
      openInput() {
        let self = this;
        self.isOpenInput = !self.isOpenInput;
        if (self.isOpenInput) {
          self.$gtag.event( 'Open', {
            'event_category': 'BloodGlucoseCard',
            'event_label': 'Opened card',
            'value': 0
          })
        }
        if (!self.isOpenInput) {
          self.$gtag.event( 'Closed',{
            'event_category': 'BloodGlucoseCard',
            'event_label': 'Closed card',
            'value': 0
          })
        }
      },
      closeInput() {
        let self = this;
        this.model = {
          tags: []
        };
        self.isOpenInput = false;
        self.$gtag.event('Closed',{
          'event_category': 'BloodGlucoseCard',
          'event_label': 'Closed card',
          'value': 0
        })
      },
      goToDetail() {
        let self = this;
        self.$router.push({
          path: '/tracking/blood-glucose/'
        })
      }
    },
    beforeDestroy() {
      this.$EventBus.$off('bgPusherReceived');
      this.$EventBus.$off('bgReadingUpdated');
      this.$EventBus.$off('lc:settingsUpdated')
    }
  }
</script>
<style lang="less">
  @import "./blood-glucose.less";
</style>



// WEBPACK FOOTER //
// BloodGlucose.vue?782d3d3d